import React, { useEffect, useState, useCallback } from 'react'
import {
  Snackbar as SnackbarMui,
  SnackbarOrigin,
  Button,
  IconButton,
  Alert,
  useMediaQuery,
  Theme,
  AlertColor,
  Box,
  AlertTitle
} from '@mui/material'
import { Close } from '@mui/icons-material'

import { ANIMATION_DURATION, SNACKBAR_WIDTH } from './internals/constants'
import { SnackbarSettings } from './types'

export default function Snackbar({
  id,
  alertTitle,
  message,
  dismissAfter = 5000,
  width = SNACKBAR_WIDTH,
  variant = 'standard',
  severity = 'default',
  anchorOrigin,
  sx,
  action,
  onAction,
  onRemove,
  actionText = 'Action'
}: SnackbarSettings) {
  const [isOpen, setIsOpen] = useState(false)
  const onMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const [snackBarPosition, setSnackBarPosition] = useState<SnackbarOrigin>(
    anchorOrigin || {
      vertical: 'bottom',
      horizontal: 'left'
    }
  )

  useEffect(() => {
    if (onMobile && !anchorOrigin) {
      setSnackBarPosition({ vertical: 'bottom', horizontal: 'center' })
    }
  }, [anchorOrigin, onMobile])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [onRemove])

  useEffect(() => {
    let currentTimer: NodeJS.Timeout | null = null
    setIsOpen(true)

    if (dismissAfter) {
      currentTimer = setTimeout(() => {
        close()
      }, dismissAfter)
    }

    return () => {
      if (currentTimer) {
        clearTimeout(currentTimer)
      }
    }
  }, [close, dismissAfter])

  // eslint-disable-next-line no-empty-function
  const handleSnackBarClose = (reason?: string) => {
    if (reason === 'timeout' || reason === 'clickaway') {
      close()
    }
  }

  const handleAction = () => {
    onAction?.()
    handleSnackBarClose('clickaway')
  }

  const renderAction = () => {
    let actionNode = action
    if (!action && actionText) {
      actionNode = (
        <Button onClick={() => handleAction()} size="small">
          {actionText}
        </Button>
      )
    }
    if (typeof action !== 'undefined') {
      if (typeof action === 'string') {
        actionNode = (
          <Button onClick={() => handleAction()} size="small">
            {action}
          </Button>
        )
      }
    }
    return (
      <>
        {(onAction || action) && <Box>{actionNode}</Box>}
        <IconButton size="small" onClick={() => handleSnackBarClose('clickaway')}>
          <Close sx={{ fontSize: 'inherit' }} />
        </IconButton>
      </>
    )
  }

  return (
    <SnackbarMui
      id={id}
      action={severity === 'default' && renderAction()}
      anchorOrigin={snackBarPosition}
      autoHideDuration={dismissAfter || null}
      message={message}
      onClose={() => handleSnackBarClose('timeout')}
      open={isOpen}
      sx={{
        position: 'relative',
        width: `${width}px`,
        ...sx
      }}
      transitionDuration={{
        enter: ANIMATION_DURATION + 100,
        exit: ANIMATION_DURATION + 400
      }}
      TransitionProps={{
        in: isOpen,
        onExited: onRemove
      }}
    >
      {severity === 'default' ? undefined : (
        <Alert severity={severity as AlertColor} variant={variant} action={renderAction()}>
          {alertTitle && <AlertTitle>{alertTitle}</AlertTitle>}
          {message}
        </Alert>
      )}
    </SnackbarMui>
  )
}
